/**
 * LegCardMedia — player headshot and team logo sub-components for LegCard
 */

import { TeamLogoFallback } from '@components/ui/TeamLogo';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { TeamWithName } from '@parlay-picker/shared/teams';
import { getTeamAbbr } from '@parlay-picker/shared/teams';
import { User } from 'lucide-react';
import type React from 'react';

// =============================================================================
// HeadshotFallback
// =============================================================================

export function HeadshotFallback(): React.ReactElement {
  return (
    <div className="w-10 h-10 rounded-full bg-theme-border flex items-center justify-center">
      <User className="w-6 h-6 text-theme-text-secondary" />
    </div>
  );
}

// =============================================================================
// LegHeadshot
// =============================================================================

interface LegHeadshotProps {
  player: string | null | undefined;
  headshotUrl: string | null;
  headshotError: boolean;
  onError: () => void;
}

export function LegHeadshot({
  player,
  headshotUrl,
  headshotError,
  onError,
}: LegHeadshotProps): React.ReactElement | null {
  if (!player) return null;

  if (headshotError || !headshotUrl) {
    return <HeadshotFallback />;
  }

  return (
    <img
      src={headshotUrl}
      alt={player}
      className="w-10 h-10 object-cover rounded-full"
      onError={onError}
    />
  );
}

// =============================================================================
// LegTeamLogo
// =============================================================================

interface LegTeamLogoProps {
  team: string | null | undefined;
  teamData: TeamWithName | null;
  teamLogoError: boolean;
  leagueKey: LeagueKey;
  onError: () => void;
}

export function LegTeamLogo({
  team,
  teamData,
  teamLogoError,
  leagueKey,
  onError,
}: LegTeamLogoProps): React.ReactElement | null {
  if (!team) return null;

  if (!teamData || teamLogoError || !teamData.logo) {
    return <TeamLogoFallback abbr={getTeamAbbr(team, leagueKey)} />;
  }

  return (
    <img src={teamData.logo} alt={team} className="w-10 h-10 object-contain" onError={onError} />
  );
}

// =============================================================================
// LegGameLogos
// =============================================================================

interface LegGameLogosProps {
  awayName: string | undefined;
  homeName: string | undefined;
  awayTeamData: TeamWithName | null;
  homeTeamData: TeamWithName | null;
  awayLogoError: boolean;
  homeLogoError: boolean;
  leagueKey: LeagueKey;
  onAwayError: () => void;
  onHomeError: () => void;
}

export function LegGameLogos({
  awayName,
  homeName,
  awayTeamData,
  homeTeamData,
  awayLogoError,
  homeLogoError,
  leagueKey,
  onAwayError,
  onHomeError,
}: LegGameLogosProps): React.ReactElement | null {
  if (!awayName && !homeName) return null;

  const renderOneLogo = (
    data: TeamWithName | null,
    name: string | undefined,
    error: boolean,
    onError: () => void,
  ): React.ReactElement => {
    if (!data || !data.logo || error) {
      return <TeamLogoFallback abbr={getTeamAbbr(name, leagueKey)} />;
    }
    return (
      <img
        src={data.logo}
        alt={name || ''}
        className="w-10 h-10 object-contain"
        onError={onError}
      />
    );
  };

  return (
    <div className="flex items-center gap-1">
      {renderOneLogo(awayTeamData, awayName, awayLogoError, onAwayError)}
      {renderOneLogo(homeTeamData, homeName, homeLogoError, onHomeError)}
    </div>
  );
}
